import React, { useState, useEffect } from 'react';
import { FileTextIcon, RefreshIcon, TrashIcon, InfoIcon, LayersIcon, DatabaseIcon, FilterIcon, CalendarIcon, XIcon, DownloadIcon, CheckIcon, AlertIcon, ChevronLeftIcon, ChevronRightIcon } from './Icons';
import { useNotification } from './Notification';

function LogsViewer({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [scriptFilter, setScriptFilter] = useState('');
  const [scripts, setScripts] = useState([]);
  const [total, setTotal] = useState(0);
  const { showNotification, showConfirm } = useNotification();
  const [columns, setColumns] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateStart: '',
    dateEnd: '',
    action: ''
  });
  const [columnFilters, setColumnFilters] = useState({});
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage, setLogsPerPage] = useState(50);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Détecter le scroll pour afficher le bouton "retour en haut"
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchScripts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/logs/scripts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setScripts(data);
      }
    } catch (error) {
      console.error('Erreur récupération scripts:', error);
    }
  };

  const extractDynamicColumns = (logsData) => {
    const columnSet = new Set();
    
    logsData.forEach(log => {
      console.log('Analyse log:', log.id, 'metadata:', log.metadata);
      if (log.metadata && typeof log.metadata === 'object') {
        Object.keys(log.metadata).forEach(key => {
          console.log('Clé metadata trouvée:', key);
          if (key.startsWith('colone.')) {
            const columnName = key.substring(7); // Retirer "colone."
            console.log('Colonne dynamique ajoutée:', columnName);
            columnSet.add(columnName);
          }
        });
      }
      // Ajouter les champs standard s'ils existent
      if (log.message) columnSet.add('message');
      if (log.player) columnSet.add('player');
      if (log.action) columnSet.add('action');
    });
    
    // Ordre forcé : Date/Heure (timestamp), Script, Action, Message en premier
    const priorityColumns = ['timestamp', 'script', 'action', 'message'];
    const otherColumns = Array.from(columnSet).filter(col => !priorityColumns.includes(col));
    const columns = [...priorityColumns.filter(col => col === 'timestamp' || col === 'script' || columnSet.has(col)), ...otherColumns];
    
    console.log('Colonnes finales extraites:', columns);
    return columns;
  };

  const fetchLogs = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (scriptFilter) params.append('script', scriptFilter);
      if (filters.dateStart) params.append('date_start', filters.dateStart);
      if (filters.dateEnd) params.append('date_end', filters.dateEnd);
      if (filters.action) params.append('action', filters.action);
      
      // Pagination
      if (logsPerPage !== 'all') {
        params.append('limit', logsPerPage);
        params.append('offset', (currentPage - 1) * logsPerPage);
      }

      const response = await fetch(`/api/logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
        setTotal(data.total);
        
        // Extraire les colonnes dynamiques
        const cols = extractDynamicColumns(data.logs);
        setColumns(cols);
        
        // Rafraîchir la liste des scripts
        fetchScripts();
      }
    } catch (error) {
      console.error('Erreur récupération logs:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchScripts();
  }, []);

  useEffect(() => {
    fetchLogs(true);
    // Auto-refresh toutes les 5 secondes en arrière-plan
    const interval = setInterval(() => fetchLogs(false), 5000);
    return () => clearInterval(interval);
  }, [search, scriptFilter, filters, currentPage, logsPerPage]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR');
  };

  const getCellValue = (log, column) => {
    if (column === 'timestamp') {
      return formatDate(log.timestamp);
    }
    if (column === 'script') {
      return log.script;
    }
    
    // Vérifier si c'est une colonne dynamique
    if (log.metadata && log.metadata[`colone.${column}`]) {
      return log.metadata[`colone.${column}`];
    }
    
    // Sinon, utiliser les champs standards
    return log[column] || '-';
  };

  const formatColumnName = (column) => {
    const names = {
      'timestamp': 'Date/Heure',
      'script': 'Script',
      'message': 'Message',
      'player': 'Joueur',
      'action': 'Action'
    };
    return names[column] || column.charAt(0).toUpperCase() + column.slice(1);
  };

  const getFilteredLogs = () => {
    if (Object.keys(columnFilters).length === 0) return logs;
    
    return logs.filter(log => {
      return Object.entries(columnFilters).every(([column, filterValue]) => {
        if (!filterValue) return true;
        const cellValue = String(getCellValue(log, column)).toLowerCase();
        return cellValue.includes(filterValue.toLowerCase());
      });
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      dateStart: '',
      dateEnd: '',
      action: ''
    });
  };

  const hasActiveFilters = () => {
    return filters.dateStart || filters.dateEnd || filters.action;
  };

  const handleRowClick = (log) => {
    setSelectedLog(log);
    setIsPanelOpen(true);
  };

  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const closePanel = () => {
    setIsPanelOpen(false);
    setTimeout(() => setSelectedLog(null), 300);
  };

  const handleDeleteLog = async (logId) => {
    const confirmed = await showConfirm(`Êtes-vous sûr de vouloir supprimer le log #${logId} ?`);
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/logs/${logId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Retirer le log de la liste
        setLogs(prevLogs => prevLogs.filter(log => log.id !== logId));
        setTotal(prev => prev - 1);
        closePanel();
        showNotification('Log supprimé avec succès', 'success');
      } else {
        const data = await response.json();
        showNotification(`Erreur: ${data.error || 'Échec de la suppression'}`, 'error');
      }
    } catch (error) {
      console.error('Erreur suppression log:', error);
      showNotification('Erreur lors de la suppression', 'error');
    }
  };

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedLogs([]);
  };

  const toggleSelectLog = (logId) => {
    setSelectedLogs(prev => 
      prev.includes(logId) 
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const toggleSelectAll = () => {
    const filteredLogs = getFilteredLogs();
    if (selectedLogs.length === filteredLogs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(filteredLogs.map(log => log.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLogs.length === 0) return;
    
    const confirmed = await showConfirm(`Êtes-vous sûr de vouloir supprimer ${selectedLogs.length} log(s) ?`);
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/logs/batch-delete', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ logIds: selectedLogs })
      });

      if (response.ok) {
        setLogs(prevLogs => prevLogs.filter(log => !selectedLogs.includes(log.id)));
        setTotal(prev => prev - selectedLogs.length);
        setSelectedLogs([]);
        setSelectMode(false);
        showNotification(`${selectedLogs.length} log(s) supprimé(s) avec succès`, 'success');
      } else {
        const data = await response.json();
        showNotification(`Erreur: ${data.error || 'Échec de la suppression'}`, 'error');
      }
    } catch (error) {
      console.error('Erreur suppression logs:', error);
      showNotification('Erreur lors de la suppression', 'error');
    }
  };

  const handleExportSelected = async () => {
    if (selectedLogs.length === 0) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/logs/batch-export', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ logIds: selectedLogs })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const dateStr = new Date().toISOString().split('T')[0];
        a.download = `logs_selection_${dateStr}.db`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showNotification(`${selectedLogs.length} log(s) exporté(s) avec succès`, 'success');
      } else {
        const data = await response.json();
        showNotification(`Erreur: ${data.error || 'Échec de l\'export'}`, 'error');
      }
    } catch (error) {
      console.error('Erreur export logs:', error);
      showNotification('Erreur lors de l\'export', 'error');
    }
  };

  const renderMetadataSection = (metadata) => {
    if (!metadata || typeof metadata !== 'object') return null;

    const dynamicColumns = {};
    const otherFields = {};

    Object.keys(metadata).forEach(key => {
      if (key.startsWith('colone.')) {
        dynamicColumns[key.substring(7)] = metadata[key];
      } else {
        otherFields[key] = metadata[key];
      }
    });

    // Trier les clés par ordre alphabétique
    const sortedDynamicColumns = Object.keys(dynamicColumns).sort().reduce((acc, key) => {
      acc[key] = dynamicColumns[key];
      return acc;
    }, {});

    const sortedOtherFields = Object.keys(otherFields).sort().reduce((acc, key) => {
      acc[key] = otherFields[key];
      return acc;
    }, {});

    return (
      <>
        {Object.keys(sortedDynamicColumns).length > 0 && (
          <div className="detail-section">
            <h4><LayersIcon size={16} /> Colonnes Dynamiques ({Object.keys(sortedDynamicColumns).length})</h4>
            {Object.entries(sortedDynamicColumns).map(([key, value]) => (
              <div key={key} className="detail-item">
                <span className="detail-label">{key}:</span>
                <span className="detail-value">{value}</span>
              </div>
            ))}
          </div>
        )}
        {Object.keys(sortedOtherFields).length > 0 && (
          <div className="detail-section">
            <h4><DatabaseIcon size={16} /> Autres Métadonnées ({Object.keys(sortedOtherFields).length})</h4>
            {Object.entries(sortedOtherFields).map(([key, value]) => (
              <div key={key} className="detail-item">
                <span className="detail-label">{key}:</span>
                <span className="detail-value">{typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}</span>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const totalPages = logsPerPage === 'all' ? 1 : Math.ceil(total / logsPerPage);

  const Pagination = () => {
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      
      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) pages.push(i);
          pages.push('...');
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push('...');
          for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(totalPages);
        }
      }
      return pages;
    };

    const startItem = logsPerPage === 'all' ? 1 : (currentPage - 1) * logsPerPage + 1;
    const endItem = logsPerPage === 'all' ? total : Math.min(currentPage * logsPerPage, total);

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Affichage {startItem} - {endItem} sur {total} log(s)
        </div>
        <div className="pagination-controls">
          <select 
            value={logsPerPage} 
            onChange={(e) => {
              const value = e.target.value === 'all' ? 'all' : Number(e.target.value);
              setLogsPerPage(value);
              setCurrentPage(1);
            }}
            className="logs-per-page"
          >
            <option value={20}>20 par page</option>
            <option value={50}>50 par page</option>
            <option value={100}>100 par page</option>
            <option value={500}>500 par page</option>
            <option value={1000}>1000 par page</option>
            <option value="all">Tout</option>
          </select>
          
          {logsPerPage !== 'all' && totalPages > 1 && (
            <div className="pagination-buttons">
              <button 
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                <ChevronLeftIcon size={16} />
                <ChevronLeftIcon size={16} style={{marginLeft: '-8px'}} />
              </button>
              <button 
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                <ChevronLeftIcon size={16} />
              </button>
              
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                ) : (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                )
              ))}
              
              <button 
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                <ChevronRightIcon size={16} />
              </button>
              <button 
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                <ChevronRightIcon size={16} />
                <ChevronRightIcon size={16} style={{marginLeft: '-8px'}} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="logs-viewer">
      <div className="logs-header">
        <h2>
          <FileTextIcon size={28} />
          Logs Nexa RP | FA
        </h2>
        <div>
          <button className="btn btn-primary" onClick={() => fetchLogs(true)}>
            <RefreshIcon size={18} />
            Actualiser
          </button>
        </div>
      </div>

      <div className="logs-controls">
        <input
          type="text"
          placeholder="Rechercher dans les logs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={scriptFilter} onChange={(e) => setScriptFilter(e.target.value)}>
          <option value="">Tous les scripts</option>
          {scripts.map(script => (
            <option key={script} value={script}>{script}</option>
          ))}
        </select>
        <button 
          className={`btn ${showAdvancedFilters ? 'btn-warning' : 'btn-secondary'}`}
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
        >
          <FilterIcon size={18} />
          Filtres {hasActiveFilters() && `(${Object.values(filters).filter(v => v).length})`}
        </button>
        <button 
          className={`btn ${selectMode ? 'btn-warning' : 'btn-secondary'}`}
          onClick={toggleSelectMode}
        >
          <LayersIcon size={18} />
          {selectMode ? 'Annuler sélection' : 'Sélectionner'}
        </button>
      </div>

      {/* Barre d'actions pour sélection multiple */}
      {selectMode && (
        <div className="selection-actions-bar">
          <div className="selection-info">
            <strong>{selectedLogs.length}</strong> log(s) sélectionné(s)
          </div>
          <div className="selection-buttons">
            <button 
              className="btn btn-secondary"
              onClick={toggleSelectAll}
            >
              <LayersIcon size={18} />
              {selectedLogs.length === getFilteredLogs().length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
            {selectedLogs.length > 0 && (
              <>
                <button 
                  className="btn btn-primary"
                  onClick={handleExportSelected}
                >
                  <DownloadIcon size={18} />
                  Exporter
                </button>
                {user?.role === 'admin' && (
                  <button 
                    className="btn btn-danger"
                    onClick={handleDeleteSelected}
                  >
                    <TrashIcon size={18} />
                    Supprimer
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <Pagination />

      {showAdvancedFilters && (
        <div className="advanced-filters">
          <div className="filters-header">
            <h3><FilterIcon size={20} /> Filtres Avancés</h3>
            {hasActiveFilters() && (
              <button className="btn-clear-filters" onClick={clearFilters}>
                <XIcon size={16} />
                Réinitialiser
              </button>
            )}
          </div>
          <div className="filters-grid">
            <div className="filter-group">
              <label>
                <CalendarIcon size={16} />
                Date de début
              </label>
              <input
                type="datetime-local"
                value={filters.dateStart}
                onChange={(e) => handleFilterChange('dateStart', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>
                <CalendarIcon size={16} />
                Date de fin
              </label>
              <input
                type="datetime-local"
                value={filters.dateEnd}
                onChange={(e) => handleFilterChange('dateEnd', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Action</label>
              <input
                type="text"
                placeholder="Filtrer par action..."
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      <p style={{ marginBottom: '20px', color: '#9ca3af' }}>
        Total: <strong>{total}</strong> log(s)
        {scriptFilter && ` - Script: ${scriptFilter}`}
        {hasActiveFilters() && ` - ${Object.values(filters).filter(v => v).length} filtre(s) actif(s)`}
      </p>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Chargement des logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="no-logs">
          <p>Aucun log disponible</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="logs-table">
            <thead>
              <tr>
                {columns.map(column => (
                  <th key={column}>{formatColumnName(column)}</th>
                ))}
              </tr>
              <tr className="filter-row">
                {columns.map(column => (
                  <th key={`filter-${column}`}>
                    <input
                      type="text"
                      placeholder="Filtrer..."
                      value={columnFilters[column] || ''}
                      onChange={(e) => handleColumnFilterChange(column, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="column-filter-input"
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {getFilteredLogs().map((log) => (
                <tr 
                  key={log.id} 
                  onClick={() => selectMode ? toggleSelectLog(log.id) : handleRowClick(log)} 
                  style={{ cursor: 'pointer' }}
                  className={selectedLogs.includes(log.id) ? 'selected-row' : ''}
                >
                  {columns.map(column => (
                    <td key={column}>{getCellValue(log, column)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination />

      {/* Panneau latéral des détails */}
      {isPanelOpen && (
        <>
          <div className={`log-detail-panel ${isPanelOpen ? 'open' : ''}`}>
            <div className="panel-header">
              <h3>Détails du Log #{selectedLog?.id}</h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button 
                  className={`btn-delete-log ${user?.role !== 'admin' ? 'disabled' : ''}`}
                  onClick={() => handleDeleteLog(selectedLog.id)}
                  disabled={user?.role !== 'admin'}
                  title={user?.role !== 'admin' ? 'Accès admin requis' : 'Supprimer ce log'}
                >
                  <TrashIcon size={16} />
                </button>
                <button className="close-panel" onClick={closePanel}>✕</button>
              </div>
            </div>
            
            {selectedLog && (
              <div className="panel-content">
                <div className="detail-section">
                  <h4><InfoIcon size={16} /> Informations Générales</h4>
                  <div className="detail-item">
                    <span className="detail-label">ID:</span>
                    <span className="detail-value">#{selectedLog.id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Script:</span>
                    <span className="detail-value script-badge">{selectedLog.script}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Date/Heure:</span>
                    <span className="detail-value">{formatDate(selectedLog.timestamp)}</span>
                  </div>
                  {selectedLog.message && (
                    <div className="detail-item">
                      <span className="detail-label">Message:</span>
                      <span className="detail-value">{selectedLog.message}</span>
                    </div>
                  )}
                  {selectedLog.action && (
                    <div className="detail-item">
                      <span className="detail-label">Action:</span>
                      <span className="detail-value action-badge">{selectedLog.action}</span>
                    </div>
                  )}
                  {selectedLog.player && (
                    <div className="detail-item">
                      <span className="detail-label">Joueur:</span>
                      <span className="detail-value">{selectedLog.player}</span>
                    </div>
                  )}
                </div>

                {selectedLog.metadata && renderMetadataSection(selectedLog.metadata)}

                {selectedLog.details && (
                  <div className="detail-section">
                    <h4><FileTextIcon size={16} /> Détails Supplémentaires</h4>
                    <pre className="details-json">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Bouton retour en haut */}
      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop} title="Retour en haut">
          ↑
        </button>
      )}
    </div>
  );
}

export default LogsViewer;
